import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Cart drawer "You may also like" upsell (snippets/p80-cart-upsell.liquid).
 *
 * Adds a card's variant to the cart and lets the cart drawer re-render itself
 * through the same CartLinesUpdateEvent flow the product form uses, so the line
 * items, free-shipping bar, totals and this list all refresh together. On mobile
 * it also drives the one-card carousel pager.
 *
 * @extends {HTMLElement}
 */
class P80CartUpsell extends HTMLElement {
    connectedCallback() {
        this.addEventListener('click', this.#handleClick);
        this.#list?.addEventListener('scroll', this.#updatePager, { passive: true });
    }

    disconnectedCallback() {
        this.removeEventListener('click', this.#handleClick);
        this.#list?.removeEventListener('scroll', this.#updatePager);
    }

    get #list() {
        return this.querySelector('[data-upsell-list]');
    }

    /** @param {MouseEvent} event */
    #handleClick = (event) => {
        const target = /** @type {HTMLElement} */ (event.target);
        const addButton = target.closest('[data-upsell-add]');
        if (addButton instanceof HTMLButtonElement) {
            this.#add(addButton);
            return;
        }

        const stepButton = target.closest('[data-upsell-step]');
        if (stepButton instanceof HTMLButtonElement) {
            const list = this.#list;
            if (!list) return;
            list.scrollBy({ left: Number(stepButton.dataset.upsellStep) * list.clientWidth });
        }
    };

    #updatePager = () => {
        const list = this.#list;
        if (!list || !list.clientWidth) return;
        const total = list.children.length;
        const index = Math.min(total - 1, Math.round(list.scrollLeft / list.clientWidth));
        const count = this.querySelector('[data-upsell-count]');
        if (count) count.textContent = `${index + 1} / ${total}`;
        const [prev, next] = this.querySelectorAll('[data-upsell-step]');
        if (prev instanceof HTMLButtonElement) prev.disabled = index === 0;
        if (next instanceof HTMLButtonElement) next.disabled = index === total - 1;
    };

    /** @param {HTMLButtonElement} button */
    async #add(button) {
        if (button.getAttribute('aria-busy') === 'true') return;
        const variantId = button.dataset.variantId;
        if (!variantId) return;

        button.setAttribute('aria-busy', 'true');
        const sectionIds = [...document.querySelectorAll('cart-items-component')]
            .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : ''))
            .filter(Boolean);
        const deferred = CartLinesUpdateEvent.createPromise();

        this.dispatchEvent(
            new CartLinesUpdateEvent({
                action: 'add',
                context: 'cart',
                lines: [{ merchandiseId: variantId, quantity: 1 }],
                promise: deferred.promise
            })
        );

        try {
            const response = await fetch(Theme.routes.cart_add_url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                body: JSON.stringify({
                    items: [{ id: Number(variantId), quantity: 1 }],
                    sections: sectionIds.join(',')
                })
            }).then((res) => res.json());

            const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());
            const didError = Boolean(response.status);

            if (didError) {
                this.#announce(response.description || response.message);
                this.dispatchEvent(
                    new CartErrorEvent({
                        error: response.message || 'Add to cart failed',
                        code: 'INVALID',
                        detail: { description: response.description, errors: response.errors }
                    })
                );
            } else {
                this.#announce(Theme.translations?.added || 'Added');
            }

            deferred.resolve({
                cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
                detail: {
                    items: cart.items,
                    source: 'p80-cart-upsell',
                    itemCount: 1,
                    sections: didError ? undefined : response.sections,
                    didError
                }
            });
        } catch (error) {
            deferred.reject(error);
            this.dispatchEvent(
                new CartErrorEvent({
                    error:
                        error instanceof Error ? error.message : 'Network error during add to cart',
                    code: 'SERVICE_UNAVAILABLE'
                })
            );
        } finally {
            button.removeAttribute('aria-busy');
        }
    }

    /** @param {string} message */
    #announce(message) {
        const status = this.querySelector('[data-upsell-status]');
        if (status) status.textContent = message;
    }
}

if (!customElements.get('p80-cart-upsell')) {
    customElements.define('p80-cart-upsell', P80CartUpsell);
}
